import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Tv, Loader2, Sparkles, UserCircle2, Coins, Plus, ChevronLeft, Clapperboard, Lock, Download, Wand2, Scissors, AlertTriangle, Wrench } from "lucide-react";
import api, { mediaUrl, downloadUrl } from "@/lib/api";
import { VIDEO_STYLES } from "@/lib/options";
import { Chip } from "@/components/Chip";
import StatusBadge from "@/components/StatusBadge";
import { TrailerDialog, TrailerResult } from "@/components/TrailerDialog";

const charReady = (c) => c && c.photo_path && c.voice_path;
const normName = (n) => (n || "").trim().toLowerCase().replace(/\s+/g, " ");
// Personajes con el MISMO nombre (ej. "Carlos" y "Carlos ") -> se marcan para no elegir el equivocado.
const dupNames = (chars) => {
  const n = {};
  chars.forEach((c) => { const k = normName(c.name); n[k] = (n[k] || 0) + 1; });
  return new Set(Object.keys(n).filter((k) => n[k] > 1));
};
const QUALITY_LABEL = { pro: "Pro 1080p", standard: "Estándar 768p" };

export default function SeriesPanel({ projects, characters, reload }) {
  const [view, setView] = useState("list");           // "list" | "series"
  const [seriesList, setSeriesList] = useState([]);
  const [sel, setSel] = useState(null);               // full selected series (with episodes)
  const [busy, setBusy] = useState(false);

  // create-series form
  const [title, setTitle] = useState("");
  const [castIds, setCastIds] = useState([]);
  const [style, setStyle] = useState("cinematic");
  const [aspect, setAspect] = useState("16:9");
  const [quality, setQuality] = useState("pro");

  // episode form
  const [idea, setIdea] = useState("");
  const [guion, setGuion] = useState("");
  const [minutes, setMinutes] = useState(1);
  const [extraIds, setExtraIds] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [epBusy, setEpBusy] = useState(false);
  const [canConfirm, setCanConfirm] = useState(false);
  const [epAspect, setEpAspect] = useState("16:9");
  const [epQuality, setEpQuality] = useState("pro");
  const [fixInfo, setFixInfo] = useState(null);       // { id, cost } — completar escenas / ensamblar gratis
  const [fixing, setFixing] = useState(false);
  const [trailerFor, setTrailerFor] = useState(null);

  useEffect(() => {
    if (!preview) { setCanConfirm(false); return; }
    setCanConfirm(false);
    const t = setTimeout(() => setCanConfirm(true), 1800);  // evita clic accidental = gasto real en Fal
    return () => clearTimeout(t);
  }, [preview]);

  const charById = (id) => characters.find((c) => c.id === id);
  const dups = dupNames(characters);
  const lowBal = !!fixInfo && !fixInfo.cost.admin && fixInfo.cost.charge_credits > (fixInfo.cost.balance ?? 0);

  const loadList = useCallback(async () => {
    try { const r = await api.get("/series"); setSeriesList(r.data || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openSeries = async (sid) => {
    setBusy(true);
    try {
      const r = await api.get(`/series/${sid}`);
      setSel(r.data); setView("series");
      setIdea(""); setGuion(""); setScenes([]); setPreview(null); setExtraIds([]);
      setEpAspect(r.data.aspect || "16:9"); setEpQuality(r.data.quality || "pro");
    } catch (e) { toast.error("No se pudo abrir la serie"); } finally { setBusy(false); }
  };

  const createSeries = async () => {
    if (!title.trim()) { toast.error("Ponle un título a la serie"); return; }
    if (castIds.length === 0) { toast.error("Elige al menos un personaje para el reparto"); return; }
    setBusy(true);
    try {
      const r = await api.post("/series", { title: title.trim(), character_ids: castIds, style, aspect, quality });
      toast.success("Serie creada");
      setTitle(""); setCastIds([]);
      await loadList();
      await openSeries(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la serie"); } finally { setBusy(false); }
  };

  const genGuion = async () => {
    if (!idea.trim()) { toast.error("Escribe la idea del capítulo"); return; }
    setEpBusy(true);
    try {
      const names = (sel.character_ids || []).map((id) => charById(id)?.name).filter(Boolean).join(", ");
      const r = await api.post("/ai/guion", { idea, style: sel.style, characters: names });
      setGuion(r.data.guion || "");
      toast.success("Guion listo. Revísalo y genera las tomas.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el guion"); } finally { setEpBusy(false); }
  };

  const fullCast = () => {
    const ids = [...(sel.character_ids || []), ...extraIds];
    return [...new Set(ids)];
  };

  const buildScenes = async (quiet = false) => {
    if (!guion.trim()) { toast.error("Primero escribe o genera el guion"); return null; }
    setEpBusy(true); setPreview(null);
    try {
      const cast = fullCast().map((id) => charById(id)).filter(Boolean);
      const names = cast.map((c) => c.name).join(", ");
      const r = await api.post("/ai/storyboard", { synopsis: guion, minutes: Number(minutes || 1), style: sel.style, characters: names, typed: true });
      const list = (r.data.scenes || []).map((s, i) => {
        const type = s.type === "dialogue" ? "dialogue" : "ambient";
        // map speaker name -> character id (ready = has photo+voice)
        let speaker_id = null;
        if (type === "dialogue" && s.speaker) {
          const m = cast.find((c) => c.name.toLowerCase() === String(s.speaker).toLowerCase() && charReady(c));
          speaker_id = m ? m.id : (cast.find((c) => charReady(c))?.id || null);
        }
        return { id: i, description: s.description || "", duration: s.duration || "6",
                 type: speaker_id ? "dialogue" : "ambient", dialogue: s.dialogue || "", speaker: s.speaker || "",
                 speaker_id, character_ids: speaker_id ? [speaker_id] : fullCast() };
      });
      setScenes(list);
      const dlg = list.filter((s) => s.type === "dialogue").length;
      if (!quiet) toast.success(`${list.length} tomas (${dlg} con diálogo). Ahora calcula el costo.`);
      return list;
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron dividir las tomas"); return null; } finally { setEpBusy(false); }
  };

  // Siempre disponible con guion: divide en tomas (gratis) si hace falta y calcula el costo EXACTO.
  const calcCost = async () => {
    const list = scenes.length > 0 ? scenes : await buildScenes(true);
    if (!list || list.length === 0) return;
    setEpBusy(true);
    try {
      const r = await api.post("/shortfilms/preview", {
        title: "Capítulo", character_ids: fullCast(), quality: epQuality, style: sel.style, aspect: epAspect,
        scenes: list.map((s) => ({ description: s.description, duration: s.duration, type: s.type,
          dialogue: s.dialogue, speaker_id: s.speaker_id, character_ids: s.character_ids })),
      });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular el costo"); } finally { setEpBusy(false); }
  };

  const openFix = async (e) => {
    try {
      const r = await api.get(`/projects/${e.id}/retry_cost`);
      setFixInfo({ id: e.id, cost: r.data });
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo calcular el costo"); }
  };

  const assembleOnly = async () => {
    setFixing(true);
    try {
      await api.post(`/projects/${fixInfo.id}/assemble`);
      setFixInfo(null);
      toast.success("Uniendo las escenas listas en el video final (gratis)…");
      await reload();
      await openSeries(sel.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo unir el video"); } finally { setFixing(false); }
  };

  const confirmFix = async () => {
    setFixing(true);
    try {
      await api.post(`/projects/${fixInfo.id}/retry?confirm=true`);
      setFixInfo(null);
      toast.success(fixInfo.cost?.pending?.length ? "Generando solo las escenas que faltan…" : "Ensamblando el video final (gratis)…");
      await reload();
      await openSeries(sel.id);
    } catch (err) {
      const d = err?.response?.data?.detail || "No se pudo iniciar";
      if (err?.response?.status === 402) toast.error(d, { action: { label: "Recargar", onClick: () => window.location.assign("/precios") } });
      else toast.error(d);
    } finally { setFixing(false); }
  };

  const generateEpisode = async () => {
    setEpBusy(true);
    try {
      await api.post("/shortfilms", {
        title: "Capítulo", series_id: sel.id, character_ids: fullCast(),
        style: sel.style, quality: epQuality, aspect: epAspect,
        continuity: true, anchor_face: true, auto_assemble: true,
        scenes: scenes.map((s) => ({ description: s.description, duration: s.duration, type: s.type,
          dialogue: s.dialogue, speaker_id: s.speaker_id, character_ids: s.character_ids })),
      });
      toast.success("¡Generando tu capítulo! Lo verás abajo y en Proyectos.");
      setPreview(null); setScenes([]); setGuion(""); setIdea(""); setExtraIds([]);
      await reload();
      await openSeries(sel.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el capítulo"); } finally { setEpBusy(false); }
  };

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  // live episode status from projects (so it updates while generating)
  const liveEpisodes = (sel?.episodes || []).map((e) => projects.find((p) => p.id === e.id) || e)
    .sort((a, b) => (a.meta?.episode_number || 0) - (b.meta?.episode_number || 0));

  if (view === "list") {
    return (
      <div className="max-w-3xl mx-auto space-y-6 fade-up">
        <div className="flex items-center gap-3">
          <Tv className="w-6 h-6 text-[#FF334B]" />
          <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Series — capítulo diario</h2>
        </div>
        <p className="text-sm text-[#94A3B8]">Fija tu reparto una vez; luego cada día solo escribes el guion y se genera el capítulo (mismos rostros y voces, con lip-sync real), numerado automáticamente.</p>

        {seriesList.length > 0 && (
          <div className="space-y-2" data-testid="series-list">
            {seriesList.map((s) => (
              <button key={s.id} data-testid={`series-${s.id}`} onClick={() => openSeries(s.id)}
                className="w-full flex items-center justify-between gap-3 mfp-card p-4 hover:border-[#FF334B]/50 transition-colors text-left">
                <span className="inline-flex items-center gap-2 font-head font-semibold"><Tv className="w-4 h-4 text-[#FF9F2E]" /> {s.title}</span>
                <span className="text-xs text-[#64748B]">{s.episode_count} capítulo{s.episode_count === 1 ? "" : "s"}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mfp-card p-6 space-y-5">
          <div className="font-head font-semibold text-lg">Nueva serie</div>
          <div>
            <label className="mono-label">Título de la serie</label>
            <input data-testid="series-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Eriberto y Carlos"
              className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
          </div>
          <div>
            <label className="mono-label">Reparto fijo (personajes de la serie)</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {characters.map((c) => (
                <button key={c.id} data-testid={`series-cast-${c.id}`} onClick={() => toggle(castIds, setCastIds, c.id)}
                  className={`flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full border transition-colors ${castIds.includes(c.id) ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>
                  {c.photo_path ? <img src={mediaUrl(c.photo_path)} alt={c.name} className="w-7 h-7 rounded-full object-cover" /> : <UserCircle2 className="w-7 h-7" />}
                  <span className="text-sm font-medium">{c.name}</span>
                  {charReady(c) ? <span className="text-[10px] text-emerald-400">voz✓</span> : <span className="inline-flex items-center gap-0.5 text-[10px] text-[#FF9F2E]"><Lock className="w-3 h-3" /> sin voz</span>}
                  {dups.has(normName(c.name)) && <span className="text-[10px] px-1.5 rounded-full bg-[#FF9F2E]/15 text-[#FF9F2E]" data-testid={`series-cast-dup-${c.id}`}>duplicado</span>}
                </button>
              ))}
            </div>
            {characters.length === 0 && <p className="text-xs text-[#FF9F2E] mt-2">Crea personajes con foto y voz en la pestaña Biblia para que puedan hablar.</p>}
            {dups.size > 0 && <p className="text-xs text-[#FF9F2E] mt-2" data-testid="series-dup-warning">Tienes personajes repetidos con el mismo nombre. Elige el que dice «voz✓» (puede hablar) y borra el otro en la pestaña Biblia.</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="mono-label">Formato</label>
              <div className="mt-3 flex gap-2">
                <Chip active={aspect === "16:9"} onClick={() => setAspect("16:9")} testId="series-aspect-169">Horizontal 16:9</Chip>
                <Chip active={aspect === "9:16"} onClick={() => setAspect("9:16")} testId="series-aspect-916">Vertical 9:16</Chip>
              </div>
            </div>
            <div>
              <label className="mono-label">Calidad</label>
              <div className="mt-3 flex gap-2">
                <Chip active={quality === "standard"} onClick={() => setQuality("standard")} testId="series-q-std">Estándar</Chip>
                <Chip active={quality === "pro"} onClick={() => setQuality("pro")} testId="series-q-pro">Pro 1080p</Chip>
              </div>
            </div>
          </div>
          <div>
            <label className="mono-label">Estilo visual</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {VIDEO_STYLES.map((s) => <Chip key={s.id} active={style === s.id} onClick={() => setStyle(s.id)} testId={`series-style-${s.id}`}>{s.label}</Chip>)}
            </div>
          </div>
          <button data-testid="series-create-btn" onClick={createSeries} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 spin" /> : <Plus className="w-5 h-5" />} Crear serie
          </button>
        </div>
      </div>
    );
  }

  // ---- series detail ----
  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <button data-testid="series-back" onClick={() => { setView("list"); setSel(null); loadList(); }}
        className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" /> Mis series
      </button>
      <div className="flex items-center gap-3">
        <Tv className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">{sel.title}</h2>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-[#94A3B8]">
        {(sel.character_ids || []).map((id) => (
          <span key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10">
            {charById(id)?.photo_path ? <img src={mediaUrl(charById(id).photo_path)} alt="" className="w-5 h-5 rounded-full object-cover" /> : <UserCircle2 className="w-4 h-4" />}
            {charById(id)?.name || "—"}
          </span>
        ))}
        <span className="px-2.5 py-1 rounded-full border border-white/10" data-testid="series-defaults">Por defecto: {sel.aspect} · {QUALITY_LABEL[sel.quality] || sel.quality}</span>
      </div>

      {/* New episode */}
      <div className="mfp-card p-6 space-y-5">
        <div className="font-head font-semibold text-lg inline-flex items-center gap-2"><Clapperboard className="w-5 h-5 text-[#FF9F2E]" /> Nuevo capítulo (Capítulo {sel.next_episode})</div>
        <div>
          <label className="mono-label">Idea de hoy (opcional, para que la IA escriba el guion)</label>
          <div className="mt-2 flex gap-2">
            <input data-testid="ep-idea-input" value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="¿Qué pasa en el capítulo de hoy?"
              className="flex-1 bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF9F2E] transition-colors text-sm" />
            <button data-testid="ep-genguion-btn" onClick={genGuion} disabled={epBusy}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 rounded-xl border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
              {epBusy && !scenes.length && !preview ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4" />} Guion IA
            </button>
          </div>
        </div>
        <div>
          <label className="mono-label">Guion del capítulo</label>
          <textarea data-testid="ep-guion-input" value={guion} onChange={(e) => setGuion(e.target.value)} rows={6}
            placeholder="Escribe el guion con los diálogos de cada personaje por su nombre…"
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors text-sm" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="mono-label">Duración (min)</label>
            <div className="mt-3 flex gap-2">
              {[1, 2, 3, 5, 7].map((m) => <Chip key={m} active={minutes === m} onClick={() => { setMinutes(m); setScenes([]); setPreview(null); }} testId={`ep-min-${m}`}>{m}</Chip>)}
            </div>
          </div>
          <div>
            <label className="mono-label">Añadir personaje a ESTE capítulo</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {characters.filter((c) => !(sel.character_ids || []).includes(c.id)).map((c) => (
                <button key={c.id} data-testid={`ep-extra-${c.id}`} onClick={() => { toggle(extraIds, setExtraIds, c.id); setScenes([]); setPreview(null); }}
                  className={`flex items-center gap-1.5 pl-1 pr-3 py-1 rounded-full border text-sm transition-colors ${extraIds.includes(c.id) ? "bg-[#FF9F2E] border-[#FF9F2E] text-black" : "border-white/12 text-[#94A3B8] hover:border-[#FF9F2E]/50"}`}>
                  {c.photo_path ? <img src={mediaUrl(c.photo_path)} alt="" className="w-6 h-6 rounded-full object-cover" /> : <UserCircle2 className="w-6 h-6" />}
                  {c.name}
                  {!charReady(c) && <span className="text-[10px] opacity-80">· sin voz</span>}
                  {dups.has(normName(c.name)) && <span className="text-[10px] opacity-80">· duplicado</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="mono-label">Formato de ESTE capítulo</label>
            <div className="mt-3 flex gap-2">
              <Chip active={epAspect === "16:9"} onClick={() => { setEpAspect("16:9"); setPreview(null); }} testId="ep-aspect-169">Horizontal 16:9</Chip>
              <Chip active={epAspect === "9:16"} onClick={() => { setEpAspect("9:16"); setPreview(null); }} testId="ep-aspect-916">Vertical 9:16</Chip>
            </div>
          </div>
          <div>
            <label className="mono-label">Calidad de ESTE capítulo</label>
            <div className="mt-3 flex gap-2">
              <Chip active={epQuality === "standard"} onClick={() => { setEpQuality("standard"); setPreview(null); }} testId="ep-q-std">Estándar 768p</Chip>
              <Chip active={epQuality === "pro"} onClick={() => { setEpQuality("pro"); setPreview(null); }} testId="ep-q-pro">Pro 1080p</Chip>
            </div>
            <p className="text-[11px] text-[#64748B] mt-2" data-testid="ep-model-hint">
              {epQuality === "pro" ? "Tomas con personaje: Kling 2.5 Turbo Pro (5s). Diálogos: Kling Avatar v2 Pro (lip-sync). Paisajes sin personaje: Hailuo-02."
                : "Tomas: Hailuo-02 768p (6s). Diálogos: Kling Avatar v2 Pro (lip-sync)."}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="ep-build-btn" onClick={() => buildScenes()} disabled={epBusy || !guion.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-white/15 text-[#CBD5E1] hover:border-[#FF334B] hover:text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-50">
            {epBusy && scenes.length === 0 ? <Loader2 className="w-5 h-5 spin" /> : <Scissors className="w-5 h-5" />} Dividir en tomas (gratis)
          </button>
          <button data-testid="ep-cost-btn" onClick={calcCost} disabled={epBusy || !guion.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 font-semibold py-3 rounded-full transition-colors disabled:opacity-50">
            {epBusy && scenes.length > 0 ? <Loader2 className="w-5 h-5 spin" /> : <Coins className="w-5 h-5" />} Calcular costo
          </button>
        </div>
        <p className="text-[11px] text-[#64748B]">Dividir en tomas y calcular el costo es GRATIS. No se genera ni se cobra nada hasta que confirmes el costo.</p>
        {scenes.length > 0 && <p className="text-xs text-[#64748B]" data-testid="ep-scenes-count">{scenes.length} tomas · {scenes.filter((s) => s.type === "dialogue").length} con diálogo (lip-sync real)</p>}
      </div>

      {/* Episodes list */}
      {liveEpisodes.length > 0 && (
        <div className="space-y-3" data-testid="episodes-list">
          <div className="mono-label">Capítulos</div>
          {liveEpisodes.map((e) => {
            const sc = e.scenes || [];
            const okScenes = sc.filter((x) => x.video_path).length;
            const missing = sc.length - okScenes;
            const noFinal = e.status === "ready" && !e.result?.export_path;
            const needsFix = e.status === "ready" && (missing > 0 || noFinal || e.result?.export_stale);
            const n = e.meta?.episode_number || e.id;
            return (
            <div key={e.id} className="mfp-card p-4 space-y-3" data-testid={`episode-${n}`}>
              <div className="flex items-center justify-between">
                <span className="font-head font-semibold">Capítulo {e.meta?.episode_number || "?"}</span>
                <StatusBadge status={e.status} />
              </div>
              <div className="flex flex-wrap gap-1.5 text-[11px] text-[#94A3B8]" data-testid={`episode-meta-${n}`}>
                <span className="px-2 py-0.5 rounded-full border border-white/10">{okScenes}/{sc.length} escenas</span>
                <span className="px-2 py-0.5 rounded-full border border-white/10">{e.meta?.aspect || "16:9"}</span>
                <span className="px-2 py-0.5 rounded-full border border-white/10">{QUALITY_LABEL[e.meta?.quality] || e.meta?.quality}</span>
              </div>
              {e.status === "ready" && e.error && (
                <p className="text-xs text-[#FF9F2E] flex items-start gap-1.5" data-testid={`episode-note-${n}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {e.error}</p>
              )}
              {needsFix && (
                <button data-testid={`episode-fix-${n}`} onClick={() => openFix(e)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF9F2E]/60 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
                  <Wrench className="w-4 h-4" /> {missing > 0 ? `Completar ${missing} escena${missing === 1 ? "" : "s"} faltante${missing === 1 ? "" : "s"}` : "Ensamblar video (gratis)"}
                </button>
              )}
              {(e.status === "processing" || e.status === "queued") && (
                <div className="flex items-center gap-2 text-sm text-[#94A3B8]"><Loader2 className="w-4 h-4 spin text-[#FF9F2E]" /> {e.progress?.label || "Generando…"} {e.progress?.total ? `(${e.progress.current}/${e.progress.total})` : ""}</div>
              )}
              {e.status === "failed" && <p className="text-sm text-red-400">{e.error}</p>}
              {e.status === "ready" && e.result?.export_path && (
                <>
                  <video src={mediaUrl(e.result.export_path)} controls poster={mediaUrl(e.result?.poster_path)} className="w-full rounded-lg border border-white/10 bg-black" data-testid={`episode-video-${e.meta?.episode_number || e.id}`} />
                  <a href={downloadUrl(e.result.export_path, `${sel.title}-Cap${e.meta?.episode_number || ""}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF334B] hover:bg-[#E0223A] text-white transition-colors">
                    <Download className="w-4 h-4" /> Descargar
                  </a>
                </>
              )}
              {e.status === "ready" && okScenes >= 2 && !e.busy && (
                <button data-testid={`episode-trailer-btn-${n}`} onClick={() => setTrailerFor(e)}
                  className="ml-2 inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF334B]/50 text-[#FF7A8A] hover:bg-[#FF334B]/10 transition-colors">
                  <Clapperboard className="w-4 h-4" /> {e.result?.trailer_path ? "Nuevo tráiler" : "Tráiler 30 s"}
                </button>
              )}
              <TrailerResult project={e} filename={`${sel.title}-Cap${e.meta?.episode_number || ""}`} />
            </div>
            );
          })}
        </div>
      )}

      {/* Cost modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ep-cost-modal" onClick={() => setPreview(null)}>
          <div className="mfp-card max-w-md w-full p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2"><Coins className="w-5 h-5 text-[#FF9F2E]" /><h3 className="font-head text-xl font-bold">Confirma el costo del capítulo</h3></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#94A3B8]">Tomas</span><strong data-testid="ep-cost-scenes">{preview.scenes} ({preview.dialogue_count} con diálogo)</strong></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Duración real aprox.</span><strong data-testid="ep-cost-seconds">{Math.floor(preview.seconds / 60)}:{String(preview.seconds % 60).padStart(2, "0")} min</strong></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Formato · calidad</span><strong>{preview.aspect} · {QUALITY_LABEL[preview.quality] || preview.quality}</strong></div>
              <div className="rounded-lg border border-white/10 p-2.5 space-y-1" data-testid="ep-cost-models">
                <div className="text-[11px] text-[#64748B]">Modelos de video que se usarán</div>
                {(preview.models || []).map((m) => (
                  <div key={m.model} className="flex justify-between text-xs"><span className="text-[#CBD5E1]">{m.model}</span><span className="text-[#94A3B8]">{m.count} toma{m.count === 1 ? "" : "s"}</span></div>
                ))}
              </div>
              {(preview.breakdown || []).length > 0 && (
                <details className="text-xs" data-testid="ep-cost-breakdown">
                  <summary className="cursor-pointer text-[#94A3B8]">Ver cada toma</summary>
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1 pr-1">
                    {preview.breakdown.map((b) => (
                      <div key={b.scene} className="flex justify-between gap-2"><span className="text-[#94A3B8]">Toma {b.scene} · {b.type} · {b.seconds}s</span><span className="text-[#64748B] truncate">{b.model} · ${b.usd}</span></div>
                    ))}
                  </div>
                </details>
              )}
              <div className="h-px bg-white/10 my-1" />
              <div className="flex justify-between text-base"><span className="text-[#94A3B8]">Costo real en Fal</span><strong className="text-[#FF334B]" data-testid="ep-cost-usd">${preview.total_usd} USD</strong></div>
              <p className="text-[11px] text-[#64748B]">Las escenas de diálogo usan lip-sync real (avatar de voz). Se factura a tu cuenta de Fal.</p>
            </div>
            <div className="flex gap-3">
              <button data-testid="ep-cost-cancel" onClick={() => setPreview(null)} className="flex-1 py-3 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">Cancelar</button>
              <button data-testid="ep-cost-confirm" onClick={generateEpisode} disabled={epBusy || !canConfirm}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60">
                {epBusy ? <Loader2 className="w-5 h-5 spin" /> : <Sparkles className="w-5 h-5" />} {canConfirm ? "Generar capítulo" : "Revisa el costo…"}
              </button>
            </div>
          </div>
        </div>
      )}

      {trailerFor && <TrailerDialog project={trailerFor} onClose={() => setTrailerFor(null)} onStarted={reload} />}

      {fixInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ep-fix-modal" onClick={() => setFixInfo(null)}>
          <div className="mfp-card max-w-md w-full p-6 space-y-4" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-2"><Wrench className="w-5 h-5 text-[#FF9F2E]" /><h3 className="font-head text-xl font-bold">Completar el capítulo</h3></div>
            {fixInfo.cost.reuse_count > 0 && <p className="text-sm text-emerald-400">✓ {fixInfo.cost.reuse_count} escena(s) ya generada(s) se reutilizan GRATIS.</p>}
            {fixInfo.cost.invalid_voice?.length > 0 && (
              <p className="text-sm text-red-300">Voz inválida en: {fixInfo.cost.invalid_voice.map((b) => `escena ${b.scene}${b.speaker ? ` (${b.speaker})` : ""}`).join(", ")}. Sube un audio real para ese personaje antes de seguir.</p>
            )}
            {fixInfo.cost.pending?.length > 0 ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-1 text-sm max-h-60 overflow-y-auto">
                {fixInfo.cost.pending.map((x) => (
                  <div key={x.scene} className="flex justify-between gap-2"><span className="text-[#94A3B8]">Escena {x.scene} · {x.type} · {x.seconds}s</span><span className="text-[#64748B] text-xs">{x.model}</span></div>
                ))}
                <div className="flex justify-between border-t border-white/10 pt-1.5 font-semibold"><span>Costo real en Fal</span><span className="text-[#FF334B]" data-testid="ep-fix-usd">${fixInfo.cost.total_usd} USD</span></div>
                <div className="flex justify-between font-semibold"><span>Créditos a cobrar</span>
                  <span data-testid="ep-fix-credits">{fixInfo.cost.admin ? "0 (admin)" : `${fixInfo.cost.charge_credits} cr`}</span></div>
                {!fixInfo.cost.admin && fixInfo.cost.balance != null && (
                  <div className={`flex justify-between text-xs ${fixInfo.cost.balance < fixInfo.cost.charge_credits ? "text-red-300" : "text-[#64748B]"}`} data-testid="ep-fix-balance">
                    <span>Tu saldo</span><span>{fixInfo.cost.balance} cr</span></div>
                )}
              </div>
            ) : (
              <p className="text-sm text-emerald-300" data-testid="ep-fix-free">Todas las escenas están listas: se unirá el video final GRATIS (no se genera ni se cobra nada).</p>
            )}
            {fixInfo.cost.pending?.length > 0 && fixInfo.cost.reuse_count > 0 && (
              <button data-testid="ep-fix-assemble-only" onClick={assembleOnly} disabled={fixing}
                className="w-full py-2.5 rounded-full border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                Unir solo las {fixInfo.cost.reuse_count} escenas listas (gratis, sin generar nada)
              </button>
            )}
            {lowBal && (
              <a href="/precios" data-testid="ep-fix-recharge" className="block text-center text-sm font-semibold text-red-300 underline">
                Saldo insuficiente — Recargar créditos
              </a>
            )}
            <div className="flex gap-3">
              <button data-testid="ep-fix-cancel" onClick={() => setFixInfo(null)} className="flex-1 py-3 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">Cancelar</button>
              <button data-testid="ep-fix-confirm" onClick={confirmFix} disabled={fixing || !fixInfo.cost.can_retry || lowBal}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF9F2E] text-black font-semibold py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50">
                {fixing ? <Loader2 className="w-5 h-5 spin" /> : <Wrench className="w-5 h-5" />} {fixInfo.cost.pending?.length ? "Confirmar y generar" : "Ensamblar (gratis)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

